import { useMutation } from '@tanstack/react-query';
import { startRegistration } from '@simplewebauthn/browser';
import { useApi } from '../../lib/api';

export interface WebAuthnRegisterResult {
  id: string;
  message: string;
}

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface WebAuthnError extends Error {
  status?: number;
  body?: ErrorBody;
}

interface RegisterStartOptions {
  publicKey?: Parameters<typeof startRegistration>[0]['optionsJSON'];
}

/**
 * Drives the WebAuthn registration ceremony for the second-factor (`mfa`) flow:
 *   1. POST /auth/webauthn/register/start  → CredentialCreationOptions
 *   2. navigator.credentials.create() (via @simplewebauthn/browser)
 *   3. POST /auth/webauthn/register/finish (with `used_as: "mfa"`)
 *
 * Authenticated flow only — the backend has no setup-token webauthn endpoint.
 */
export function useWebAuthnMfaRegister() {
  const api = useApi();
  return useMutation<WebAuthnRegisterResult, WebAuthnError, { name?: string } | void>({
    mutationFn: async (input) => {
      const startRes = await api.POST('/auth/webauthn/register/start', {});
      if (startRes.error) {
        const e = new Error('webauthn_start_failed') as WebAuthnError;
        e.status = startRes.response?.status;
        e.body = (startRes.error ?? {}) as ErrorBody;
        throw e;
      }
      const optionsResp = (startRes.data?.options ?? {}) as unknown as RegisterStartOptions;
      // The server returns the full PublicKeyCredentialCreationOptions wrapped
      // in `{ publicKey: { ... } }` (webauthn-rs standard shape). Unwrap if
      // present so we hand `startRegistration` just the options object.
      const optionsJSON = (
        optionsResp && typeof optionsResp === 'object' && 'publicKey' in optionsResp
          ? optionsResp.publicKey
          : (optionsResp as unknown)
      ) as Parameters<typeof startRegistration>[0]['optionsJSON'];

      const credential = await startRegistration({ optionsJSON });

      const finishRes = await api.POST('/auth/webauthn/register/finish', {
        body: {
          credential,
          name: input?.name ?? 'Security key',
          used_as: 'mfa',
        },
      });

      if (finishRes.error) {
        const e = new Error('webauthn_finish_failed') as WebAuthnError;
        e.status = finishRes.response?.status;
        e.body = (finishRes.error ?? {}) as ErrorBody;
        throw e;
      }
      return finishRes.data as WebAuthnRegisterResult;
    },
  });
}
